import React from "react";
import { useNavigate } from "react-router-dom";
import {
  IconEye,
  IconClipboardFilled,
  IconCircleCheck,
  IconClock,
  IconClockHour4,
  IconMapPin,
} from "@tabler/icons-react";

import { formatDate } from "../../../service/convert/date";
import { IRequestItem } from "../../../store/@types/requests";
import { getRequestStatusValue } from "./requestStatus";

interface IRequestCard {
  item: IRequestItem;
}

const RequestCard: React.FC<IRequestCard> = ({ item }) => {
  const navigate = useNavigate();
  const requestStatus = getRequestStatusValue(item.request_status) || "pending";

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "text-primary-600 bg-primary-50/10";
      case "rejected":
        return "text-red-600 bg-red-50";
      case "in_review":
        return "text-blue-600 bg-blue-50/10";
      case "pending":
        return "text-yellow-600 bg-yellow-50/10";
      default:
        return "text-gray-600 bg-gray-50/10";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <IconCircleCheck size={14} className="text-green-600" />;
      case "rejected":
        return <IconClockHour4 size={14} className="text-red-600" />;
      case "in_review":
        return <IconClock size={14} className="text-blue-600" />;
      case "pending":
        return <IconClock size={14} className="text-yellow-600" />;
      default:
        return <IconClock size={14} className="text-gray-600" />;
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ");
  };

  return (
    <>
      <div className="group w-full bg-white rounded-lg shadow-sm border border-gray-100/20 hover:border-primary-200 hover:shadow-md transition-all duration-200">
        <div className="flex items-center gap-4 p-4">
          {/* Icon */}
          <div className="flex-shrink-0 p-2 bg-primary-50/20 rounded-lg">
            <IconClipboardFilled size={24} className="text-primary-500" />
          </div>

          {/* Content */}
          <div className="flex-grow min-w-0">
            {/* Title and Type */}
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {item.service ? item.service.name : item.document?.name}
              </h3>
              <div className="flex items-center gap-3">
                <span className="inline-block text-xs font-medium text-primary-600 bg-primary-50/10 px-2 py-1 rounded mt-1">
                  {item.type === "new" ? "New Request" : "Renewal"}
                </span>
                <span className="flex items-center text-sm text-gray-400 capitalize">
                  <IconMapPin size={14} className=" mr-1" />
                  {item.state.name}, {item.state.country.name}
                </span>
              </div>
            </div>

            {/* Meta Information */}
            <div className="flex flex-col lg:flex-row gap-0 lg:gap-6 mt-3 text-sm text-gray-500 ">
              <span className="flex items-center">
                <span className="mr-2 font-medium">Created:</span>
                {formatDate(new Date(item.created_at))}
              </span>
              <span className="flex items-center">
                <span className="mr-2 font-medium">Updated:</span>
                {formatDate(new Date(item.created_at))}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Status Badge */}
            <div
              className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(requestStatus)} whitespace-nowrap`}
            >
              {getStatusIcon(requestStatus)}
              <span>{getStatusLabel(requestStatus)}</span>
            </div>
            {/* Action Button */}
            <button
              onClick={() => navigate(`/dashboard/requests/${item.id}`)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 bg-gray-50/10 
                      hover:text-primary-700 transition-colors duration-200"
              aria-label="View request details"
              title="View Details"
            >
              <IconEye size={18} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RequestCard;
